import { Order } from '@/types'
import { differenceInDays } from 'date-fns'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js'
import { Bar, Doughnut } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
)

interface DeliveryMetricsProps {
  orders: Order[]
}

const calculateDeliveryStats = (orders: Order[]) => {
  const completedOrders = orders.filter(order => 
    order.status === 'completed' && order.confirmedDeliveryDate && order.actualDeliveryDate
  )

  const delayDistribution = completedOrders.reduce((acc, order) => {
    const delay = differenceInDays(order.actualDeliveryDate!, order.confirmedDeliveryDate!)
    const bucket = Math.floor(delay / 2) * 2 // Group in 2-day buckets
    acc[bucket] = (acc[bucket] || 0) + 1
    return acc
  }, {} as Record<number, number>)

  const onTimeDeliveries = completedOrders.filter(order => 
    differenceInDays(order.actualDeliveryDate!, order.confirmedDeliveryDate!) <= 0
  ).length

  const onTimePercentage = completedOrders.length > 0 
    ? (onTimeDeliveries / completedOrders.length) * 100 
    : 0

  return {
    delayDistribution,
    onTimePercentage,
    totalCompleted: completedOrders.length,
  }
}

export function DeliveryMetrics({ orders }: DeliveryMetricsProps) {
  const { delayDistribution, onTimePercentage, totalCompleted } = calculateDeliveryStats(orders)

  const delayBuckets = Object.keys(delayDistribution)
    .map(Number)
    .sort((a, b) => a - b)

  const histogramData = {
    labels: delayBuckets.map(bucket => 
      bucket === 0 ? 'On time' : 
      bucket < 0 ? `${Math.abs(bucket)}-${Math.abs(bucket) + 2} days early` :
      `${bucket}-${bucket + 2} days late`
    ),
    datasets: [
      {
        label: 'Number of Orders',
        data: delayBuckets.map(bucket => delayDistribution[bucket]),
        backgroundColor: delayBuckets.map(bucket =>
          bucket <= 0 ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'
        ),
        borderColor: delayBuckets.map(bucket =>
          bucket <= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
        ),
        borderWidth: 1,
      },
    ],
  }

  const gaugeData = {
    labels: ['On Time', 'Delayed'],
    datasets: [
      {
        data: [onTimePercentage, 100 - onTimePercentage],
        backgroundColor: [
          'rgba(34, 197, 94, 0.5)',
          'rgba(239, 68, 68, 0.5)',
        ],
        borderColor: [
          'rgb(34, 197, 94)',
          'rgb(239, 68, 68)',
        ],
        borderWidth: 1,
      },
    ],
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          On-Time Delivery Rate
        </h3>
        <div className="relative h-64">
          <Doughnut
            data={gaugeData}
            options={{
              cutout: '75%',
              circumference: 180,
              rotation: -90,
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.formattedValue}% of orders`,
                  },
                },
              },
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {Math.round(onTimePercentage)}%
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                On Time
              </div>
            </div>
          </div>
        </div>
        <div className="text-center mt-4 text-sm text-gray-500 dark:text-gray-400">
          Based on {totalCompleted} completed orders
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Delivery Delay Distribution
        </h3>
        <div className="h-64">
          <Bar
            data={histogramData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  callbacks: {
                    label: (context) => `${context.formattedValue} orders`,
                  },
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  ticks: {
                    stepSize: 1,
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </div>
  )
} 